import React, { useState, useEffect, useCallback } from "react";
import "./ConfigEditor.css";

function ConfigEditor() {
    const [config, setConfig] = useState(null);
    const [activeSection, setActiveSection] = useState("company");
    const [activeLanguage, setActiveLanguage] = useState("nl");
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState(null);
    const [expandedCategories, setExpandedCategories] = useState({});

    // Check authorization
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get("token");
        const expectedToken = process.env.REACT_APP_ADMIN_TOKEN || "admin2024";

        if (token === expectedToken) {
            setIsAuthorized(true);
        }

        // Listen for postMessage from parent iframe
        const handleMessage = (event) => {
            const allowedOrigins = (process.env.REACT_APP_ADMIN_ALLOWED_ORIGINS || "").split(",");
            if (allowedOrigins.includes(event.origin) || event.origin === window.location.origin) {
                if (event.data.type === "INIT_CONFIG") {
                    setConfig(event.data.payload.config);
                    setIsAuthorized(true);
                }
                if (event.data.type === "SAVE_CONFIRMED") {
                    setIsSaving(false);
                    setSaveMessage({ type: "success", text: "Config opgeslagen!" });
                }
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    // Load config
    useEffect(() => {
        if (isAuthorized && !config) {
            // Check localStorage first for cached admin config
            const cachedConfig = localStorage.getItem("config_admin_cache");
            if (cachedConfig) {
                try {
                    setConfig(JSON.parse(cachedConfig));
                    return;
                } catch (e) {
                    console.warn("Invalid cached config, loading from server");
                }
            }

            fetch("/config.json")
                .then((res) => res.json())
                .then((data) => setConfig(data))
                .catch((err) => console.error("Failed to load config:", err));
        }
    }, [isAuthorized, config]);

    // Notify parent of changes
    const notifyParent = useCallback((newConfig) => {
        if (window.parent !== window) {
            window.parent.postMessage({ type: "CONFIG_UPDATED", payload: { config: newConfig } }, "*");
        }
    }, []);

    // Update config value
    const updateValue = (path, value) => {
        const newConfig = JSON.parse(JSON.stringify(config));
        const pathParts = path.split(".");
        let current = newConfig;

        for (let i = 0; i < pathParts.length - 1; i++) {
            if (!current[pathParts[i]]) {
                current[pathParts[i]] = {};
            }
            current = current[pathParts[i]];
        }
        current[pathParts[pathParts.length - 1]] = value;

        setConfig(newConfig);
        notifyParent(newConfig);
    };

    // Get nested value
    const getValue = (path, defaultValue = "") => {
        const pathParts = path.split(".");
        let current = config;

        for (const part of pathParts) {
            if (current === undefined || current === null) return defaultValue;
            current = current[part];
        }
        return current ?? defaultValue;
    };

    // Save config
    const handleSave = async () => {
        setIsSaving(true);
        setSaveMessage(null);

        if (window.parent !== window) {
            // In iframe mode, notify parent
            window.parent.postMessage({ type: "CONFIG_SAVE_REQUEST", payload: { config } }, "*");
        } else {
            // Try to save to server via API endpoint (works with WAMP/Apache)
            try {
                const response = await fetch("/api/save-config.php", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ config }),
                });

                // Check if we got a valid JSON response (PHP is running)
                const contentType = response.headers.get("content-type");
                if (contentType && contentType.includes("application/json")) {
                    const result = await response.json();
                    if (result.success) {
                        localStorage.setItem("config_admin_cache", JSON.stringify(config));
                        setSaveMessage({ type: "success", text: "Config opgeslagen!" });
                    } else {
                        setSaveMessage({ type: "error", text: `Fout bij opslaan: ${result.error}` });
                    }
                } else {
                    // PHP not available (React dev server) - save to localStorage only
                    localStorage.setItem("config_admin_cache", JSON.stringify(config));
                    setSaveMessage({ type: "warning", text: "Config opgeslagen in browser (geen PHP). Voor permanente opslag: yarn build en open via WAMP." });
                }
            } catch (error) {
                console.error("Save error:", error);
                // Fallback to localStorage
                localStorage.setItem("config_admin_cache", JSON.stringify(config));
                setSaveMessage({ type: "warning", text: "Config opgeslagen in browser (server niet bereikbaar)." });
            } finally {
                setIsSaving(false);
            }
        }
    };

    // Export config
    const handleExport = () => {
        const blob = new Blob([JSON.stringify(config, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "config.json";
        a.click();
        URL.revokeObjectURL(url);
    };

    // Reset to server config (clear localStorage cache)
    const handleReset = () => {
        if (window.confirm("Wil je de lokale wijzigingen verwijderen en de server config opnieuw laden?")) {
            localStorage.removeItem("config_admin_cache");
            setConfig(null);
            setSaveMessage(null);
        }
    };

    // Toggle category expansion
    const toggleCategory = (categoryKey) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryKey]: !prev[categoryKey]
        }));
    };

    if (!isAuthorized) {
        return (
            <div className="config-editor">
                <div className="auth-error">
                    <h1>Toegang geweigerd</h1>
                    <p>Je hebt geen toegang tot de config editor.</p>
                    <p>Voeg <code>?token=YOUR_TOKEN</code> toe aan de URL.</p>
                </div>
            </div>
        );
    }

    if (!config) {
        return (
            <div className="config-editor">
                <div className="loading">Config laden...</div>
            </div>
        );
    }

    const languages = config.meta?.supportedLanguages || ["nl", "fr", "en"];
    const langPath = `translations.${activeLanguage}`;

    const sections = [
        { id: "company", label: "Bedrijf", icon: "building" },
        { id: "branding", label: "Branding", icon: "palette" },
        { id: "links", label: "Links", icon: "link" },
        { id: "onboarding", label: "Onboarding", icon: "rocket" },
        { id: "tracker", label: "Tracker", icon: "map" },
        { id: "dialogs", label: "Dialogen", icon: "message" },
        { id: "toasts", label: "Meldingen", icon: "bell" },
        { id: "menu", label: "Menu Pagina", icon: "utensils" },
        { id: "about", label: "About Pagina", icon: "info" },
        { id: "info", label: "Info Pagina", icon: "file" },
        { id: "faq", label: "FAQ", icon: "question" },
        { id: "contact", label: "Contact", icon: "phone" },
        { id: "analytics", label: "Analytics", icon: "chart" },
    ];

    // Handle image upload
    const handleImageUpload = async (path, file) => {
        if (!file) return;

        // Check file type
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            alert('Alleen PNG, JPG, GIF en WebP afbeeldingen zijn toegestaan.');
            return;
        }

        // Check file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert('Bestand is te groot. Maximum grootte: 5MB');
            return;
        }

        setSaveMessage({ type: "info", text: `Afbeelding "${file.name}" uploaden...` });

        // Try to upload to server
        const formData = new FormData();
        formData.append('image', file);
        formData.append('directory', 'images');

        try {
            const response = await fetch('/api/upload-image.php', {
                method: 'POST',
                body: formData,
            });

            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                const result = await response.json();
                if (result.success) {
                    updateValue(path, result.path);
                    setSaveMessage({
                        type: "success",
                        text: `Afbeelding "${result.filename}" geüpload!`
                    });
                } else {
                    setSaveMessage({
                        type: "error",
                        text: `Upload mislukt: ${result.error}`
                    });
                }
            } else {
                // PHP not available (React dev server) - use local preview only
                const reader = new FileReader();
                reader.onload = () => {
                    // Store filename as path (user must copy file manually)
                    const targetPath = `/images/${file.name}`;
                    updateValue(path, targetPath);
                    setSaveMessage({
                        type: "warning",
                        text: `Geen server upload (dev mode). Kopieer "${file.name}" handmatig naar public/images/`
                    });
                };
                reader.readAsDataURL(file);
            }
        } catch (error) {
            console.error('Upload error:', error);
            // Fallback for when server is not available
            const targetPath = `/images/${file.name}`;
            updateValue(path, targetPath);
            setSaveMessage({
                type: "warning",
                text: `Upload server niet bereikbaar. Kopieer "${file.name}" handmatig naar public/images/`
            });
        }
    };

    const renderInput = (label, path, type = "text", options = {}) => {
        const value = getValue(path, options.defaultValue || "");

        if (type === "textarea") {
            return (
                <div className="field-group">
                    <label>{label}</label>
                    <textarea
                        value={value}
                        onChange={(e) => updateValue(path, e.target.value)}
                        rows={options.rows || 3}
                    />
                    {options.hint && <span className="field-hint">{options.hint}</span>}
                </div>
            );
        }

        if (type === "color") {
            return (
                <div className="field-group">
                    <label>{label}</label>
                    <div className="color-picker-row">
                        <input
                            type="color"
                            value={value || "#000000"}
                            onChange={(e) => updateValue(path, e.target.value)}
                            className="color-swatch"
                        />
                        <input
                            type="text"
                            value={value}
                            onChange={(e) => updateValue(path, e.target.value)}
                            placeholder="#000000"
                            className="color-text"
                        />
                        <div className="color-preview" style={{ backgroundColor: value || '#000000' }}></div>
                    </div>
                </div>
            );
        }

        if (type === "image") {
            return (
                <div className="field-group image-field">
                    <label>{label}</label>
                    <div className="image-input-row">
                        <input
                            type="text"
                            value={value}
                            onChange={(e) => updateValue(path, e.target.value)}
                            placeholder="/images/voorbeeld.png"
                            className="image-url-input"
                        />
                        <label className="btn-upload">
                            <input
                                type="file"
                                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                                onChange={(e) => handleImageUpload(path, e.target.files[0])}
                                style={{ display: 'none' }}
                            />
                            Upload
                        </label>
                    </div>
                    {value && (
                        <div className="image-preview-container">
                            <img
                                src={value.startsWith('data:') ? value : value}
                                alt={label}
                                className="image-preview"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        </div>
                    )}
                    {options.hint && <span className="field-hint">{options.hint}</span>}
                </div>
            );
        }

        return (
            <div className="field-group">
                <label>{label}</label>
                <input
                    type={type}
                    value={value}
                    onChange={(e) => updateValue(path, e.target.value)}
                    placeholder={options.placeholder || ""}
                />
                {options.hint && <span className="field-hint">{options.hint}</span>}
            </div>
        );
    };

    const renderArrayEditor = (label, path, itemFields) => {
        const items = getValue(path, []);

        const addItem = () => {
            const newItem = {};
            itemFields.forEach(field => {
                newItem[field.key] = field.defaultValue || "";
            });
            updateValue(path, [...items, newItem]);
        };

        const removeItem = (index) => {
            const newItems = items.filter((_, i) => i !== index);
            updateValue(path, newItems);
        };

        const updateItem = (index, key, value) => {
            const newItems = [...items];
            newItems[index] = { ...newItems[index], [key]: value };
            updateValue(path, newItems);
        };

        return (
            <div className="form-group array-editor">
                <label>{label}</label>
                <div className="array-items">
                    {items.map((item, index) => (
                        <div key={index} className="array-item">
                            {itemFields.map(field => (
                                <input
                                    key={field.key}
                                    type="text"
                                    value={item[field.key] || ""}
                                    onChange={(e) => updateItem(index, field.key, e.target.value)}
                                    placeholder={field.label}
                                />
                            ))}
                            <button type="button" className="btn-remove" onClick={() => removeItem(index)}>×</button>
                        </div>
                    ))}
                </div>
                <button type="button" className="btn-add" onClick={addItem}>+ Item toevoegen</button>
            </div>
        );
    };

    const renderFAQEditor = () => {
        const faqPath = `${langPath}.infoPage.faq.categories`;
        const categories = getValue(faqPath, []);

        const addCategory = () => {
            updateValue(faqPath, [...categories, { title: "Nieuwe categorie", questions: [] }]);
        };

        const updateCategory = (catIndex, field, value) => {
            const newCategories = [...categories];
            newCategories[catIndex] = { ...newCategories[catIndex], [field]: value };
            updateValue(faqPath, newCategories);
        };

        const removeCategory = (catIndex) => {
            updateValue(faqPath, categories.filter((_, i) => i !== catIndex));
        };

        const addQuestion = (catIndex) => {
            const newCategories = [...categories];
            newCategories[catIndex].questions = [
                ...(newCategories[catIndex].questions || []),
                { question: "", answer: "" }
            ];
            updateValue(faqPath, newCategories);
        };

        const updateQuestion = (catIndex, qIndex, field, value) => {
            const newCategories = [...categories];
            newCategories[catIndex].questions[qIndex] = {
                ...newCategories[catIndex].questions[qIndex],
                [field]: value
            };
            updateValue(faqPath, newCategories);
        };

        const removeQuestion = (catIndex, qIndex) => {
            const newCategories = [...categories];
            newCategories[catIndex].questions = newCategories[catIndex].questions.filter((_, i) => i !== qIndex);
            updateValue(faqPath, newCategories);
        };

        return (
            <div className="faq-editor">
                {categories.map((category, catIndex) => {
                    const isExpanded = expandedCategories[`faq-${catIndex}`];
                    return (
                        <div key={catIndex} className="faq-category">
                            <div className="faq-category-header" onClick={() => toggleCategory(`faq-${catIndex}`)}>
                                <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
                                <input
                                    type="text"
                                    value={category.title}
                                    onChange={(e) => updateCategory(catIndex, "title", e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="Categorie titel"
                                />
                                <button type="button" className="btn-remove" onClick={(e) => { e.stopPropagation(); removeCategory(catIndex); }}>×</button>
                            </div>
                            {isExpanded && (
                                <div className="faq-questions">
                                    {(category.questions || []).map((q, qIndex) => (
                                        <div key={qIndex} className="faq-question">
                                            <input
                                                type="text"
                                                value={q.question}
                                                onChange={(e) => updateQuestion(catIndex, qIndex, "question", e.target.value)}
                                                placeholder="Vraag"
                                            />
                                            <textarea
                                                value={q.answer}
                                                onChange={(e) => updateQuestion(catIndex, qIndex, "answer", e.target.value)}
                                                placeholder="Antwoord (HTML toegestaan)"
                                                rows={2}
                                            />
                                            <button type="button" className="btn-remove" onClick={() => removeQuestion(catIndex, qIndex)}>×</button>
                                        </div>
                                    ))}
                                    <button type="button" className="btn-add" onClick={() => addQuestion(catIndex)}>+ Vraag toevoegen</button>
                                </div>
                            )}
                        </div>
                    );
                })}
                <button type="button" className="btn-add" onClick={addCategory}>+ Categorie toevoegen</button>
            </div>
        );
    };

    const renderOnboardingEditor = () => {
        const slidesPath = `${langPath}.onboarding.slides`;
        const slides = getValue(slidesPath, []);

        const updateSlide = (index, field, value) => {
            const newSlides = [...slides];
            newSlides[index] = { ...newSlides[index], [field]: value };
            updateValue(slidesPath, newSlides);
        };

        return (
            <div className="onboarding-editor">
                <h4>Onboarding Slides</h4>
                {slides.map((slide, index) => {
                    const isExpanded = expandedCategories[`slide-${index}`];
                    return (
                        <div key={index} className="slide-card">
                            <div className="slide-header" onClick={() => toggleCategory(`slide-${index}`)}>
                                <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
                                <span className="slide-type">{slide.type}</span>
                                <span className="slide-title">{slide.title}</span>
                            </div>
                            {isExpanded && (
                                <div className="slide-content">
                                    {renderInput("Titel", `${slidesPath}.${index}.title`)}
                                    {slide.type === "welcome" && (
                                        <>
                                            {renderInput("Subtitel", `${slidesPath}.${index}.subtitle`)}
                                            {renderInput("Swipe hint", `${slidesPath}.${index}.swipeHint`)}
                                        </>
                                    )}
                                    {slide.type !== "welcome" && (
                                        renderInput("Beschrijving (HTML)", `${slidesPath}.${index}.description`, "textarea", { rows: 3 })
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
                {renderInput("Start knop tekst", `${langPath}.onboarding.startButton`)}
                <h4 style={{marginTop: "1.5rem"}}>Consent</h4>
                {renderInput("Titel", `${langPath}.onboarding.consent.title`)}
                {renderInput("Beschrijving", `${langPath}.onboarding.consent.description`, "textarea")}
                {renderInput("Privacy policy link tekst", `${langPath}.onboarding.consent.privacyPolicy`)}
                {renderInput("Cookie policy link tekst", `${langPath}.onboarding.consent.cookiePolicy`)}
                {renderInput("Accepteer knop", `${langPath}.onboarding.consent.acceptButton`)}
            </div>
        );
    };

    const renderSection = () => {
        switch (activeSection) {
            case "company":
                return (
                    <>
                        <h3>Bedrijfsgegevens</h3>
                        {renderInput("Bedrijfsnaam", `${langPath}.company.name`)}
                        {renderInput("Product naam (enkelvoud)", `${langPath}.company.productName`)}
                        {renderInput("Product naam (meervoud)", `${langPath}.company.productNamePlural`)}
                    </>
                );

            case "branding":
                return (
                    <>
                        <h3>Kleuren</h3>
                        <div className="color-grid">
                            {renderInput("Primaire kleur", "shared.branding.primaryColor", "color")}
                            {renderInput("Secundaire kleur", "shared.branding.secondaryColor", "color")}
                            {renderInput("Accent kleur", "shared.branding.accentColor", "color")}
                            {renderInput("Tekst kleur", "shared.branding.textColor", "color")}
                            {renderInput("Tekst kleur (licht)", "shared.branding.textColorLight", "color")}
                        </div>
                        <h3 style={{marginTop: "1.5rem"}}>Afbeeldingen</h3>
                        <p className="section-hint">Upload afbeeldingen (PNG, JPG, GIF, WebP) of geef een URL op.</p>
                        {renderInput("Logo", "shared.images.logo", "image")}
                        {renderInput("Call button afbeelding", "shared.images.callButtonImage", "image")}
                        {renderInput("Truck beschikbaar", "shared.images.truckAvailableImage", "image")}
                        {renderInput("Truck niet beschikbaar", "shared.images.truckUnavailableImage", "image")}
                    </>
                );

            case "links":
                return (
                    <>
                        <h3>Externe Links</h3>
                        {renderInput("Website URL", "shared.links.website")}
                        {renderInput("Reservatie URL (ijskar)", "shared.links.reservation")}
                        {renderInput("Restaurant Reservatie URL", "shared.links.restaurantReservation")}
                        {renderInput("Shop URL", "shared.links.shop")}
                        {renderInput("Jobs URL", "shared.links.jobs")}
                    </>
                );

            case "onboarding":
                return (
                    <>
                        <h3>Onboarding</h3>
                        {renderOnboardingEditor()}
                    </>
                );

            case "tracker":
                return (
                    <>
                        <h3>Tracker Teksten</h3>
                        {renderInput("Call button tekst", `${langPath}.tracker.callButton.text`)}
                        {renderInput("Radar actief tekst", `${langPath}.tracker.radarButton.activeText`)}
                        {renderInput("Radar inactief tekst", `${langPath}.tracker.radarButton.inactiveText`)}

                        <h3 style={{marginTop: "1.5rem"}}>Tracker Knoppen</h3>
                        <div className="field-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={getValue("shared.tracker.homePoi.enabled", false)}
                                    onChange={(e) => updateValue("shared.tracker.homePoi.enabled", e.target.checked)}
                                    style={{marginRight: "0.5rem"}}
                                />
                                Toon "Route" knop naar thuislocatie
                            </label>
                        </div>
                        {renderInput("Thuislocatie naam", "shared.tracker.homePoi.name")}
                        {renderInput("Thuislocatie adres", "shared.tracker.homePoi.address")}
                        {renderInput("Thuislocatie latitude", "shared.tracker.homePoi.latitude")}
                        {renderInput("Thuislocatie longitude", "shared.tracker.homePoi.longitude")}

                        <div className="field-group" style={{marginTop: "1.5rem"}}>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={getValue("shared.tracker.showRestaurantButton", false)}
                                    onChange={(e) => updateValue("shared.tracker.showRestaurantButton", e.target.checked)}
                                    style={{marginRight: "0.5rem"}}
                                />
                                Toon "Reserveer" knop (restaurant)
                            </label>
                        </div>
                        <p style={{fontSize: "0.85rem", color: "#666", marginTop: "0.5rem"}}>
                            De restaurant URL wordt ingesteld bij Links → Restaurant Reservatie URL
                        </p>
                    </>
                );

            case "dialogs":
                return (
                    <>
                        <h3>Locatie Dialogen</h3>
                        {renderInput("Vraag titel", `${langPath}.dialogs.locationRequest.title`)}
                        {renderInput("Vraag bericht", `${langPath}.dialogs.locationRequest.message`, "textarea")}
                        {renderInput("Accepteer knop", `${langPath}.dialogs.locationRequest.acceptButton`)}
                        {renderInput("Weiger knop", `${langPath}.dialogs.locationRequest.denyButton`)}

                        <h3 style={{marginTop: "1.5rem"}}>Silent Call Dialoog</h3>
                        {renderInput("Titel", `${langPath}.dialogs.silentCall.title`)}
                        {renderInput("Bericht template", `${langPath}.dialogs.silentCall.messageTemplate`, "textarea")}
                        {renderInput("Bevestig knop", `${langPath}.dialogs.silentCall.confirmButton`)}
                        {renderInput("Adres wijzigen knop", `${langPath}.dialogs.silentCall.changeAddressButton`)}
                        {renderInput("Annuleer knop", `${langPath}.dialogs.silentCall.cancelButton`)}

                        <h3 style={{marginTop: "1.5rem"}}>Call Dialoog</h3>
                        {renderInput("Titel", `${langPath}.dialogs.call.title`)}
                        {renderInput("Bericht template", `${langPath}.dialogs.call.messageTemplate`, "textarea")}
                        {renderInput("Bevestig knop", `${langPath}.dialogs.call.confirmButton`)}

                        <h3 style={{marginTop: "1.5rem"}}>Beschikbaar Dialoog</h3>
                        {renderInput("Titel", `${langPath}.dialogs.availableNearby.title`)}
                        {renderInput("Bericht", `${langPath}.dialogs.availableNearby.message`, "textarea")}
                        {renderInput("Bevestig knop", `${langPath}.dialogs.availableNearby.confirmButton`)}

                        <h3 style={{marginTop: "1.5rem"}}>Niet Beschikbaar Dialoog</h3>
                        {renderInput("Titel", `${langPath}.dialogs.noAvailableNearby.title`)}
                        {renderInput("Bericht", `${langPath}.dialogs.noAvailableNearby.message`, "textarea")}
                        {renderInput("Bevestig knop", `${langPath}.dialogs.noAvailableNearby.confirmButton`)}

                        <h3 style={{marginTop: "1.5rem"}}>Te Ver Weg Dialoog</h3>
                        {renderInput("Titel", `${langPath}.dialogs.tooFarAway.title`)}
                        {renderInput("Bericht", `${langPath}.dialogs.tooFarAway.message`, "textarea")}
                    </>
                );

            case "toasts":
                return (
                    <>
                        <h3>Toast Meldingen</h3>
                        {renderInput("Silent call ingeschakeld", `${langPath}.toasts.silentCallEnabled`, "textarea")}
                        {renderInput("Silent call uitgeschakeld", `${langPath}.toasts.silentCallDisabled`, "textarea")}
                        {renderInput("Silent call fout", `${langPath}.toasts.silentCallError`, "textarea")}
                        {renderInput("Oproep succes", `${langPath}.toasts.callSuccess`, "textarea")}
                        {renderInput("Oproep fout", `${langPath}.toasts.callError`, "textarea")}
                        {renderInput("Adres onvolledig", `${langPath}.toasts.addressIncomplete`, "textarea")}
                        {renderInput("Oproep al geplaatst", `${langPath}.toasts.callAlreadyPlaced`, "textarea")}

                        <h3 style={{marginTop: "1.5rem"}}>Loaders</h3>
                        {renderInput("Even geduld", `${langPath}.loaders.pleaseWait`)}
                        {renderInput("Locatie ophalen", `${langPath}.loaders.fetchingLocation`)}
                        {renderInput("Buurt checken", `${langPath}.loaders.checkingNearby`)}
                        {renderInput("Notificatie aanvragen", `${langPath}.loaders.requestingNotification`)}
                        {renderInput("Notificatie verwijderen", `${langPath}.loaders.removingNotification`)}
                    </>
                );

            case "menu":
                return (
                    <>
                        <h3>Menu Pagina</h3>
                        {renderInput("Pagina titel", `${langPath}.menuPage.title`)}
                        {renderInput("Betaalmethodes titel", `${langPath}.menuPage.sections.paymentTitle`)}
                        {renderInput("Betaalmethodes noot", `${langPath}.menuPage.sections.paymentNote`, "textarea")}
                        {renderInput("Intro tekst", `${langPath}.menuPage.sections.introText`, "textarea")}
                        {renderInput("Menu afbeelding", `${langPath}.menuPage.sections.menuImage`, "image", { hint: "Alleen PNG en JPG bestanden. Aanbevolen formaat: 800x1200px." })}
                        {renderInput("Menu afbeelding alt", `${langPath}.menuPage.sections.menuImageAlt`)}
                    </>
                );

            case "about":
                return (
                    <>
                        <h3>About Pagina</h3>
                        {renderInput("Pagina titel", `${langPath}.aboutPage.title`)}
                        {renderInput("Betaalmethodes titel", `${langPath}.aboutPage.sections.paymentTitle`)}
                        {renderInput("Betaalmethodes noot", `${langPath}.aboutPage.sections.paymentNote`, "textarea")}
                        {renderInput("Historiek titel", `${langPath}.aboutPage.sections.historyTitle`)}
                        {renderInput("Historiek tekst 1", `${langPath}.aboutPage.sections.historyText1`, "textarea")}
                        {renderInput("Historiek tekst 2", `${langPath}.aboutPage.sections.historyText2`, "textarea")}
                        {renderInput("Waar titel", `${langPath}.aboutPage.sections.whereTitle`)}
                        {renderInput("Waar tekst", `${langPath}.aboutPage.sections.whereText`, "textarea")}
                        {renderInput("Waar noot", `${langPath}.aboutPage.sections.whereNote`, "textarea")}
                        {renderInput("Wanneer titel", `${langPath}.aboutPage.sections.whenTitle`)}
                        {renderInput("Wanneer tekst", `${langPath}.aboutPage.sections.whenText`, "textarea")}
                    </>
                );

            case "info":
                return (
                    <>
                        <h3>Info Pagina</h3>
                        {renderInput("Pagina titel", `${langPath}.infoPage.title`)}
                        {renderInput("Reservatie URL", `${langPath}.infoPage.reservationUrl`)}
                        {renderInput("Reserveer knop", `${langPath}.infoPage.reserveButton`)}

                        <h4 style={{marginTop: "1.5rem"}}>Navigatie Labels</h4>
                        {renderInput("Reserveren", `${langPath}.infoPage.navigation.reserve`)}
                        {renderInput("Live Status", `${langPath}.infoPage.navigation.liveStatus`)}
                        {renderInput("Over Bedrijf", `${langPath}.infoPage.navigation.aboutCompany`)}
                        {renderInput("Over App", `${langPath}.infoPage.navigation.aboutApp`)}
                        {renderInput("FAQ", `${langPath}.infoPage.navigation.faq`)}

                        <h4 style={{marginTop: "1.5rem"}}>Over Sectie</h4>
                        {renderInput("Historiek titel", `${langPath}.infoPage.about.historyTitle`)}
                        {renderInput("Historiek tekst", `${langPath}.infoPage.about.historyText`, "textarea")}
                        {renderInput("Waar & Wanneer titel", `${langPath}.infoPage.about.whereWhenTitle`)}
                        {renderInput("Waar tekst", `${langPath}.infoPage.about.whereText`, "textarea")}
                        {renderInput("Wanneer tekst", `${langPath}.infoPage.about.whenText`, "textarea")}

                        <h4 style={{marginTop: "1.5rem"}}>Status Labels</h4>
                        {renderInput("Beschikbaar", `${langPath}.infoPage.statusLabels.available`)}
                        {renderInput("Niet beschikbaar", `${langPath}.infoPage.statusLabels.unavailable`)}
                        {renderInput("Laden...", `${langPath}.infoPage.statusLabels.loading`)}
                    </>
                );

            case "faq":
                return (
                    <>
                        <h3>FAQ Beheer</h3>
                        <p className="section-description">Beheer de veelgestelde vragen per categorie.</p>
                        {renderFAQEditor()}
                    </>
                );

            case "contact":
                return (
                    <>
                        <h3>Contact Gegevens</h3>
                        {renderInput("Sectie titel", `${langPath}.infoPage.contact.title`)}
                        {renderInput("Vraag titel", `${langPath}.infoPage.contact.questionTitle`)}

                        <h4 style={{marginTop: "1.5rem"}}>E-mail</h4>
                        {renderInput("Label", `${langPath}.infoPage.contact.emailLabel`)}
                        {renderInput("E-mail adres", `${langPath}.infoPage.contact.email`)}

                        <h4 style={{marginTop: "1.5rem"}}>Telefoon</h4>
                        {renderInput("Label", `${langPath}.infoPage.contact.phoneLabel`)}
                        {renderInput("Telefoonnummer (voor link)", `${langPath}.infoPage.contact.phone`)}
                        {renderInput("Telefoonnummer (weergave)", `${langPath}.infoPage.contact.phoneDisplay`)}
                        {renderInput("Telefoon noot", `${langPath}.infoPage.contact.phoneNote`)}

                        <h4 style={{marginTop: "1.5rem"}}>Website</h4>
                        {renderInput("Label", `${langPath}.infoPage.contact.websiteLabel`)}
                        {renderInput("URL", `${langPath}.infoPage.contact.websiteUrl`)}
                        {renderInput("Weergave tekst", `${langPath}.infoPage.contact.websiteDisplay`)}

                        <h4 style={{marginTop: "1.5rem"}}>Social Media</h4>
                        {renderInput("Facebook URL", `${langPath}.infoPage.contact.facebookUrl`)}
                        {renderInput("Facebook naam", `${langPath}.infoPage.contact.facebookName`)}
                        {renderInput("Instagram URL", `${langPath}.infoPage.contact.instagramUrl`)}
                        {renderInput("Instagram naam", `${langPath}.infoPage.contact.instagramName`)}
                        {renderInput("TikTok URL", `${langPath}.infoPage.contact.tiktokUrl`)}
                        {renderInput("TikTok naam", `${langPath}.infoPage.contact.tiktokName`)}
                    </>
                );

            case "analytics":
                return (
                    <>
                        <h3>Analytics & Tracking</h3>
                        <p className="section-description">Configureer tracking pixels en analytics. Laat velden leeg om ze uit te schakelen.</p>

                        <h4 style={{marginTop: "1rem"}}>Google Analytics</h4>
                        <div className="analytics-card">
                            <div className="field-group checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={getValue("shared.analytics.googleAnalytics.enabled", false)}
                                        onChange={(e) => updateValue("shared.analytics.googleAnalytics.enabled", e.target.checked)}
                                    />
                                    Google Analytics inschakelen
                                </label>
                            </div>
                            {renderInput("Measurement ID (GA4)", "shared.analytics.googleAnalytics.measurementId", "text", { placeholder: "G-XXXXXXXXXX" })}
                        </div>

                        <h4 style={{marginTop: "1.5rem"}}>Meta Pixel (Facebook)</h4>
                        <div className="analytics-card">
                            <div className="field-group checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={getValue("shared.analytics.metaPixel.enabled", false)}
                                        onChange={(e) => updateValue("shared.analytics.metaPixel.enabled", e.target.checked)}
                                    />
                                    Meta Pixel inschakelen
                                </label>
                            </div>
                            {renderInput("Pixel ID", "shared.analytics.metaPixel.pixelId", "text", { placeholder: "123456789012345" })}
                        </div>

                        <h4 style={{marginTop: "1.5rem"}}>TikTok Pixel</h4>
                        <div className="analytics-card">
                            <div className="field-group checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={getValue("shared.analytics.tiktokPixel.enabled", false)}
                                        onChange={(e) => updateValue("shared.analytics.tiktokPixel.enabled", e.target.checked)}
                                    />
                                    TikTok Pixel inschakelen
                                </label>
                            </div>
                            {renderInput("Pixel ID", "shared.analytics.tiktokPixel.pixelId", "text", { placeholder: "ABCDEFGH12345678" })}
                        </div>

                        <h4 style={{marginTop: "1.5rem"}}>Google Tag Manager</h4>
                        <div className="analytics-card">
                            <div className="field-group checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={getValue("shared.analytics.gtm.enabled", false)}
                                        onChange={(e) => updateValue("shared.analytics.gtm.enabled", e.target.checked)}
                                    />
                                    Google Tag Manager inschakelen
                                </label>
                            </div>
                            {renderInput("Container ID", "shared.analytics.gtm.containerId", "text", { placeholder: "GTM-XXXXXXX" })}
                        </div>

                        <h4 style={{marginTop: "1.5rem"}}>Hotjar</h4>
                        <div className="analytics-card">
                            <div className="field-group checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={getValue("shared.analytics.hotjar.enabled", false)}
                                        onChange={(e) => updateValue("shared.analytics.hotjar.enabled", e.target.checked)}
                                    />
                                    Hotjar inschakelen
                                </label>
                            </div>
                            {renderInput("Site ID", "shared.analytics.hotjar.siteId", "text", { placeholder: "1234567" })}
                        </div>

                        <h4 style={{marginTop: "1.5rem"}}>Aangepaste Scripts</h4>
                        {renderInput("Custom head scripts", "shared.analytics.customHeadScript", "textarea", { rows: 4, hint: "HTML/JS code die in de <head> wordt geladen. Wees voorzichtig!" })}
                    </>
                );

            default:
                return <p>Selecteer een sectie</p>;
        }
    };

    return (
        <div className="config-editor">
            <header className="editor-header">
                <h1>Config Editor</h1>
                <div className="header-actions">
                    <button type="button" className="btn-reset" onClick={handleReset}>
                        Reset
                    </button>
                    <button type="button" className="btn-export" onClick={handleExport}>
                        Exporteer
                    </button>
                    <button type="button" className="btn-save" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? "Opslaan..." : "Opslaan"}
                    </button>
                </div>
            </header>

            {saveMessage && (
                <div className={`save-message ${saveMessage.type}`}>
                    {saveMessage.text}
                </div>
            )}

            <div className="editor-body">
                <aside className="editor-sidebar">
                    <div className="language-selector">
                        <label>Taal:</label>
                        <div className="language-buttons">
                            {languages.map((lang) => (
                                <button
                                    key={lang}
                                    type="button"
                                    className={`lang-btn ${activeLanguage === lang ? "active" : ""}`}
                                    onClick={() => setActiveLanguage(lang)}
                                >
                                    {lang.toUpperCase()}
                                </button>
                            ))}
                        </div>
                    </div>

                    <nav className="section-nav">
                        {sections.map((section) => (
                            <button
                                key={section.id}
                                type="button"
                                className={`nav-item ${activeSection === section.id ? "active" : ""}`}
                                onClick={() => setActiveSection(section.id)}
                            >
                                {section.label}
                            </button>
                        ))}
                    </nav>
                </aside>

                <main className="editor-main">
                    <div className="section-content">
                        {renderSection()}
                    </div>
                </main>
            </div>
        </div>
    );
}

export default ConfigEditor;
